<template>
  <div class="profile-completion-container">
    <div class="completion-card">
      <div class="completion-header">
        <div class="header-icon">👤</div>
        <h2 class="completion-title">Complete Your Profile</h2>
        <p class="completion-subtitle">Help us personalize your learning experience</p>
      </div>
      
      <form @submit.prevent="updateProfile" class="completion-form">
        <div class="form-group">
          <label for="first-name" class="form-label">First Name</label>
          <input
            id="first-name"
            v-model="firstName"
            type="text"
            placeholder="Enter your first name"
            class="form-input"
            required
          />
        </div>

        <div class="form-group">
          <label for="last-name" class="form-label">Last Name (Optional)</label>
          <input
            id="last-name"
            v-model="lastName"
            type="text"
            placeholder="Enter your last name (optional)"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="grade-level" class="form-label">Grade Level</label>
          <select 
            id="grade-level"
            v-model="gradeLevel" 
            class="form-select"
            required
          >
            <option value="">Select your grade</option>
            <option v-for="grade in grades" :key="grade" :value="grade">
              {{ grade === 'K' ? 'Kindergarten' : `Grade ${grade}` }}
            </option>
          </select>
        </div>
        
        <div class="form-group">
          <label for="state" class="form-label">State</label>
          <select 
            id="state"
            v-model="state" 
            class="form-select"
            required
          >
            <option value="">Select your state</option>
            <option v-for="stateOption in states" :key="stateOption.value" :value="stateOption.value">
              {{ stateOption.label }}
            </option>
          </select>
          <small class="form-help">We'll include state-specific questions when available</small>
        </div>
        
        <button 
          type="submit" 
          :disabled="loading || !isFormValid"
          class="submit-button"
        >
          <span v-if="loading" class="button-content">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Updating Profile...
          </span>
          <span v-else class="button-content">
            <span class="button-icon">✅</span>
            Complete Profile
          </span>
        </button>
      </form>
      
      <div class="completion-benefits">
        <h3 class="benefits-title">Why complete your profile?</h3>
        <div class="benefits-list">
          <div class="benefit-item">
            <span class="benefit-icon">🎯</span>
            <span>Get personalized questions for your grade level</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-icon">📊</span>
            <span>Track your progress with your name</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-icon">🚀</span>
            <span>Unlock the full adaptive learning experience</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

// Define props and emits at the top level
const props = defineProps({
  user: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['profile-completed']);

const firstName = ref('');
const lastName = ref('');
const gradeLevel = ref('');
const state = ref('');
const loading = ref(false);

const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const states = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const isFormValid = computed(() => {
  return firstName.value && firstName.value.trim() &&
         gradeLevel.value && gradeLevel.value.trim() &&
         state.value && state.value.trim();
});

async function updateProfile() {
  console.log('updateProfile called');
  console.log('props.user:', props.user);
  console.log('props.user type:', typeof props.user);
  console.log('props.user keys:', props.user ? Object.keys(props.user) : 'null');
  console.log('firstName.value:', firstName.value);
  console.log('lastName.value:', lastName.value);
  console.log('gradeLevel.value:', gradeLevel.value);
  console.log('state.value:', state.value);
  console.log('isFormValid.value:', isFormValid.value);
  
  if (!props.user) {
    console.log('Early return - props.user is null/undefined');
    alert('User not authenticated. Please log in again.');
    return;
  }
  
  if (!props.user.uid) {
    console.log('Early return - props.user.uid is missing');
    console.log('Available properties:', Object.keys(props.user));
    alert('User authentication incomplete. Please log in again.');
    return;
  }
  
  if (!isFormValid.value) {
    console.log('Early return - form not valid');
    alert('Please fill in all required fields.');
    return;
  }
  
  // Defensive checks for reactive variables
  if (!firstName.value || !gradeLevel.value || !state.value) {
    console.log('Defensive check failed');
    alert('Please fill in all required fields.');
    return;
  }
  
  loading.value = true;
  try {
    const userId = props.user.uid;
    if (!userId) {
      throw new Error('User ID not available');
    }
    const userRef = doc(db, 'users', userId);
    const updateData = {
      firstName: firstName.value.trim(),
      gradeLevel: gradeLevel.value,
      state: state.value,
      email: props.user.email, // Add email field
      isPremium: false, // Add isPremium field with default false
      updatedAt: new Date()
    };

    // Only add lastName if it's provided
    if (lastName.value && lastName.value.trim()) {
      updateData.lastName = lastName.value.trim();
    }

    await updateDoc(userRef, updateData);

    // Don't clear form - this is profile completion, not a multi-use form
    // The parent component will handle the UI state change

    // Emit success event
    emit('profile-completed');
    
  } catch (error) {
    console.error('Error updating profile:', error);
    
    // Check if it's a quota error
    if (error.code === 'resource-exhausted' || error.message.includes('quota') || error.message.includes('limit')) {
      alert('We\'ve reached our daily database limit. Your profile will be saved locally for now. Please try again tomorrow or contact support.');
      
      // Store profile data locally as fallback
      localStorage.setItem('pendingProfileData', JSON.stringify({
        firstName: firstName.value ? firstName.value.trim() : '',
        lastName: lastName.value ? lastName.value.trim() : '',
        gradeLevel: gradeLevel.value || '',
        state: state.value || null,
        timestamp: new Date().toISOString()
      }));
      
      // Still emit the event so the modal closes
      emit('profile-completed');
    } else {
      alert('Failed to update profile. Please try again.');
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.profile-completion-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
}

.completion-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.completion-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  text-align: center;
}

.header-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.completion-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.completion-subtitle {
  font-size: 1.125rem;
  opacity: 0.9;
}

.completion-form {
  padding: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-input, .form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-input:focus, .form-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-help {
  display: block;
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.submit-button {
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-size: 1.125rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.submit-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.submit-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.completion-benefits {
  background: #f8fafc;
  padding: 2rem;
  border-top: 1px solid #e5e7eb;
}

.benefits-title {
  font-size: 1.25rem;
  font-weight: bold;
  color: #374151;
  margin-bottom: 1.5rem;
  text-align: center;
}

.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.benefit-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .profile-completion-container {
    padding: 1rem;
  }
  
  .completion-title {
    font-size: 1.5rem;
  }
  
  .completion-subtitle {
    font-size: 1rem;
  }
}
</style> 